// src/components/Common/ReportTemplateForm.jsx
import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  FormHelperText,
  InputAdornment,
  Paper
} from '@mui/material';

const ReportTemplateForm = ({ template, values = {}, onChange }) => {
  if (!template || !template.customFields) {
    return null;
  }

  const renderField = (field) => {
    const value = values[field.fieldName] || field.defaultValue || '';

    // Check conditional display
    if (field.conditionalDisplay) {
      const dependentValue = values[field.conditionalDisplay.dependsOn];
      const shouldShow = 
        field.conditionalDisplay.condition === 'equals' 
          ? dependentValue === field.conditionalDisplay.value
          : dependentValue !== field.conditionalDisplay.value;
      
      if (!shouldShow) return null;
    }

    const handleChange = (newValue) => {
      onChange(field.fieldName, newValue);
    };

    switch (field.fieldType) {
      case 'text':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            helperText={field.helperText}
            required={field.required}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            multiline
            rows={3}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            helperText={field.helperText}
            required={field.required}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleChange(parseFloat(e.target.value) || '')}
            helperText={field.helperText}
            required={field.required}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.validation?.step || 1
            }}
          />
        );

      case 'measurement':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleChange(parseFloat(e.target.value) || '')}
            helperText={field.helperText}
            required={field.required}
            InputProps={{
              endAdornment: field.unit && (
                <InputAdornment position="end">{field.unit}</InputAdornment>
              )
            }}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.validation?.step || 0.1
            }}
          />
        );

      case 'select':
        return (
          <FormControl key={field.fieldName} fullWidth required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleChange(e.target.value)}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.helperText && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl key={field.fieldName} fullWidth required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              value={value || []}
              label={field.label}
              onChange={(e) => handleChange(e.target.value)}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.helperText && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'checkbox':
        return (
          <FormControlLabel
            key={field.fieldName}
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => handleChange(e.target.checked)}
              />
            }
            label={field.label}
          />
        );

      case 'radio':
        return (
          <FormControl key={field.fieldName} required={field.required}>
            <Typography variant="subtitle2" gutterBottom>
              {field.label}
            </Typography>
            <RadioGroup
              value={value}
              onChange={(e) => handleChange(e.target.value)}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {field.helperText && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            type="date"
            label={field.label}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            helperText={field.helperText}
            required={field.required}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'finding':
        return (
          <FormControl key={field.fieldName} fullWidth>
            <Typography variant="subtitle2" gutterBottom>
              {field.label}
            </Typography>
            <RadioGroup
              row
              value={value || 'absent'}
              onChange={(e) => handleChange(e.target.value)}
            >
              <FormControlLabel value="absent" control={<Radio />} label="Absent" />
              <FormControlLabel value="present" control={<Radio />} label="Present" />
            </RadioGroup>
          </FormControl>
        );

      default:
        return null;
    }
  };

  // Group fields by section
  const sections = {};
  template.customFields.forEach(field => {
    const section = field.section || 'Additional Findings';
    if (!sections[section]) {
      sections[section] = [];
    }
    sections[section].push(field);
  });

  return (
    <Box>
      {Object.entries(sections).map(([sectionName, fields]) => (
        <Paper key={sectionName} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            {sectionName}
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {fields
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(field => (
                <Box key={field.fieldName} sx={{ gridColumn: field.fieldType === 'textarea' ? '1 / -1' : 'auto' }}>
                  {renderField(field)}
                </Box>
              ))}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default ReportTemplateForm;